import * as React from 'react';
import { ActionHubProps } from './ActionHub.Props';
import { getActionHubStyle } from './ActionHub.styles';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { Persona, PersonaSize } from '@fluentui/react/lib/Persona';
import { getInitials } from '@fluentui/react';
import { getActionHubControlRenderer } from './getActionHubControlRenderer';
import { onRenderIcon } from '../Renderers';

const personaSize = PersonaSize.size32;
const defaultIconSize = 32;

/**
 * Control that can be used to display information about a user as well actions that can be taken in order
 * to interact with them. Used by the Coauthor Gallery to provide additional information on users it displays
 * Is tightly coupled with the Fabric Persona control that can be found here:
 * https://github.com/microsoft/fluentui/tree/f9ca45d012d2e77eea728e19651b144452562004/packages/office-ui-fabric-react/src/components/Persona
 */
export class ActionHub extends React.PureComponent<ActionHubProps, {}> {
  private iconSize: number;

  public constructor(props: ActionHubProps) {
    super(props);
    this.iconSize = this.getIconSizeFromPersonaSize(personaSize);
  }

  public render(): JSX.Element {
    const { coauthorInfo, setFocusOnMount, palette, commandsToRender, shouldRenderPersonaControl } = this.props;
    // Get styles for action hub. Pass in whether this actionhub is showing any commands
    // to see if we need a border to separate the persona from any commands
    const styles = getActionHubStyle(palette, commandsToRender.length > 0);
    return (
      <>
        {shouldRenderPersonaControl && (
          <Persona
            imageUrl={coauthorInfo.imageUrl}
            text={coauthorInfo.name}
            secondaryText={coauthorInfo.description}
            initialsColor={coauthorInfo.color}
            size={personaSize}
            showSecondaryText={true}
            styles={styles.persona}
            imageShouldStartVisible={true}
            imageInitials={getInitials(coauthorInfo.name, false)}
            onRenderInitials={
              getInitials(coauthorInfo.name, false) === '' || coauthorInfo.isAnonymous
                ? onRenderIcon(this.iconSize, this.props.fallBackUserImage)
                : undefined
            }
            showInitialsUntilImageLoads={true}
          />
        )}
        {commandsToRender.length > 0 && (
          <FocusZone allowFocusRoot={!setFocusOnMount}>
            <div role="menu">{...commandsToRender.map(getActionHubControlRenderer(styles.button))}</div>
          </FocusZone>
        )}
      </>
    );
  }

  // Gets the name of the PersonaSize enum (e.g. size32) and uses a regex to extract just the integer values and then parses that as an integer
  // Returns default icon size if enum cannot be parsed
  private getIconSizeFromPersonaSize = (personaSize: PersonaSize): number => {
    const match = PersonaSize[personaSize].match(/\d+/);
    return match ? parseInt(match[0], 10 /* radix (base 10) */) : defaultIconSize;
  };
}
